import { useQuery, useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { mediaBase } from './configs';
import type {
  NotificationsCallback,
  CreateNotificationsCallbackRequest,
  UpdateNotificationsCallbackRequest,
} from './vod-notifications-types';

// ============================================
// Query keys
// ============================================

export const vodNotificationKeys = {
  all: ['vod', 'notifications'] as const,
  list: (serviceId: string) =>
    ['vod', 'notifications', 'list', serviceId] as const,
};

// ============================================
// Queries
// ============================================

export function useNotificationCallbacks(serviceId: string) {
  return useQuery<NotificationsCallback[]>({
    queryKey: vodNotificationKeys.list(serviceId),
    enabled: !!serviceId,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${mediaBase(serviceId, 'vod')}/callbacks`);
      return res.data?.data ?? res.data;
    },
  });
}

// ============================================
// Mutations
// ============================================

export function useCreateNotification(
  serviceId: string,
  options?: UseMutationOptions<NotificationsCallback, Error, CreateNotificationsCallbackRequest>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: CreateNotificationsCallbackRequest) => {
      const res = await serviceApiV3.post(`${mediaBase(serviceId, 'vod')}/callbacks`, req);
      return res.data?.data ?? res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: vodNotificationKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}

export function useUpdateNotification(
  serviceId: string,
  options?: UseMutationOptions<
    NotificationsCallback,
    Error,
    { id: string; req: UpdateNotificationsCallbackRequest }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, req }: { id: string; req: UpdateNotificationsCallbackRequest }) => {
      const res = await serviceApiV3.put(`${mediaBase(serviceId, 'vod')}/callbacks/${id}`, req);
      return res.data?.data ?? res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: vodNotificationKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteNotification(
  serviceId: string,
  options?: UseMutationOptions<void, Error, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await serviceApiV3.delete(`${mediaBase(serviceId, 'vod')}/callbacks/${id}`);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: vodNotificationKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}
